import React from 'react';
import { Button } from '../ui/button';
import { 
  Bot, Plus, Target, Users, TrendingUp, Sparkles, Globe, 
  Mail, FileText, CheckCircle, MessageSquare, Image, Video,
  Webhook, GraduationCap, XCircle
} from 'lucide-react';

const AgentTutorial = ({ onCreateAgent }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-8" data-testid="agents-tutorial">
      {/* Header */}
      <div className="text-center pb-6 border-b border-gray-100">
        <div className="inline-flex p-4 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full mb-4">
          <Bot className="text-amber-600" size={48} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Tutorial de Agentes de IA</h2>
        <p className="text-gray-500 mt-2">Aprende a configurar y utilizar tus asistentes inteligentes</p>
      </div>

      {/* What is an Agent */}
      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 bg-amber-100 text-amber-700 rounded-full text-sm font-bold">1</span>
          ¿Qué es un Agente de IA?
        </h3>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-gray-700">
            Un agente de IA es un asistente inteligente que puede automatizar tareas en tu CRM. 
            Puede enviar emails, crear documentos, gestionar tareas, agregar notas y más — todo de forma autónoma o con tu aprobación.
          </p>
        </div>
      </section>

      {/* Agent Types */}
      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 bg-amber-100 text-amber-700 rounded-full text-sm font-bold">2</span>
          Tipos de Agentes
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
            <Target className="text-blue-600 mb-2" size={24} />
            <h4 className="font-semibold text-gray-900">Ventas</h4>
            <p className="text-sm text-gray-600">Seguimiento de leads, emails de venta, propuestas comerciales</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-100">
            <Users className="text-green-600 mb-2" size={24} />
            <h4 className="font-semibold text-gray-900">Soporte</h4>
            <p className="text-sm text-gray-600">Respuestas a consultas, seguimiento de tickets, satisfacción</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
            <TrendingUp className="text-purple-600 mb-2" size={24} />
            <h4 className="font-semibold text-gray-900">Marketing</h4>
            <p className="text-sm text-gray-600">Campañas de email, contenido promocional, redes sociales</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-100">
            <Sparkles className="text-amber-600 mb-2" size={24} />
            <h4 className="font-semibold text-gray-900">Asistente</h4>
            <p className="text-sm text-gray-600">Ayuda general, respuestas rápidas, organización</p>
          </div>
        </div>
      </section>

      {/* Global Agent */}
      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 bg-amber-100 text-amber-700 rounded-full text-sm font-bold">3</span>
          Agente Global
        </h3>
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-start gap-3">
            <Globe className="text-blue-600 flex-shrink-0" size={24} />
            <div>
              <p className="text-gray-700">
                Un <strong>agente global</strong> aparece en <strong>todas las páginas</strong> del CRM como un widget flotante. 
                Puede leer el contexto de la página actual y ayudarte con tareas específicas.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Para activar: Crea un agente y marca la opción &quot;Agente Global&quot; en la configuración.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 bg-amber-100 text-amber-700 rounded-full text-sm font-bold">4</span>
          Capacidades del Agente
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
            <Mail className="text-red-500" size={20} />
            <div>
              <p className="font-medium text-gray-900">Enviar Emails</p>
              <p className="text-xs text-gray-500">Usando templates o personalizados vía Gmail</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
            <FileText className="text-blue-500" size={20} />
            <div>
              <p className="font-medium text-gray-900">Crear Documentos</p>
              <p className="text-xs text-gray-500">Propuestas y documentos en Google Docs</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
            <CheckCircle className="text-green-500" size={20} />
            <div>
              <p className="font-medium text-gray-900">Gestionar Tareas</p>
              <p className="text-xs text-gray-500">Crear, completar y dar seguimiento</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
            <MessageSquare className="text-amber-500" size={20} />
            <div>
              <p className="font-medium text-gray-900">Agregar Notas</p>
              <p className="text-xs text-gray-500">Documentar interacciones y seguimientos</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
            <Image className="text-purple-500" size={20} />
            <div>
              <p className="font-medium text-gray-900">Generar Imágenes</p>
              <p className="text-xs text-gray-500">Crear imágenes con Nano Banana AI</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
            <Video className="text-pink-500" size={20} />
            <div>
              <p className="font-medium text-gray-900">Generar Videos</p>
              <p className="text-xs text-gray-500">Crear videos cortos con Google Veo</p>
            </div>
          </div>
        </div>
      </section>

      {/* Autonomy Levels */}
      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 bg-amber-100 text-amber-700 rounded-full text-sm font-bold">5</span>
          Niveles de Autonomía
        </h3>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-20 text-center">
                <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-sm font-medium">0%</span>
              </div>
              <div>
                <p className="text-gray-700"><strong>Supervisado:</strong> Todas las acciones requieren tu aprobación</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-20 text-center">
                <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-sm font-medium">30-60%</span>
              </div>
              <div>
                <p className="text-gray-700"><strong>Semi-autónomo:</strong> Acciones de bajo riesgo auto-aprobadas</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-20 text-center">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-medium">90-100%</span>
              </div>
              <div>
                <p className="text-gray-700"><strong>Autónomo:</strong> Ejecuta todas las acciones sin esperar aprobación</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* n8n Integration */}
      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 bg-amber-100 text-amber-700 rounded-full text-sm font-bold">6</span>
          Integración con n8n
        </h3>
        <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
          <div className="flex items-start gap-3">
            <Webhook className="text-orange-600 flex-shrink-0" size={24} />
            <div>
              <p className="text-gray-700 mb-3">
                Los agentes pueden recibir instrucciones automáticas desde <strong>n8n</strong> u otras plataformas de automatización.
              </p>
              <div className="bg-white rounded p-3 font-mono text-xs overflow-x-auto">
                <p className="text-gray-500 mb-1"># Endpoint para recibir instrucciones:</p>
                <p className="text-orange-600">POST /api/agents/n8n/execute-instruction</p>
                <p className="text-gray-500 mt-2 mb-1"># Payload de ejemplo:</p>
                <pre className="text-gray-700">{`{
  "instruction": "Envía email de seguimiento a Empresa ABC",
  "agent_id": "global",
  "empresa_id": "uuid-de-empresa",
  "execute_immediately": true
}`}</pre>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                También puedes usar <code className="bg-gray-100 px-1 rounded">/api/agents/{'{agent_id}'}/receive-task</code> para tareas estructuradas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Training */}
      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 bg-amber-100 text-amber-700 rounded-full text-sm font-bold">7</span>
          Entrenamiento del Agente
        </h3>
        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
          <div className="flex items-start gap-3">
            <GraduationCap className="text-purple-600 flex-shrink-0" size={24} />
            <div>
              <p className="text-gray-700">
                Puedes entrenar a tu agente subiendo documentos a su carpeta de Google Drive. El agente aprende de:
              </p>
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                <li className="flex items-center gap-2"><FileText size={14} /> Documentos de referencia (productos, servicios)</li>
                <li className="flex items-center gap-2"><Mail size={14} /> Ejemplos de emails exitosos</li>
                <li className="flex items-center gap-2"><MessageSquare size={14} /> Instrucciones y directivas</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} /> Acciones aprobadas (aprendizaje automático)</li>
                <li className="flex items-center gap-2"><XCircle size={14} /> Acciones rechazadas (evitar patrones)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Start */}
      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 bg-amber-100 text-amber-700 rounded-full text-sm font-bold">8</span>
          Inicio Rápido
        </h3>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <ol className="space-y-3 text-gray-700">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
              <span>Haz clic en <strong>&quot;Nuevo Agente&quot;</strong> y elige un tipo (Ventas, Soporte, etc.)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
              <span>Configura el <strong>nivel de autonomía</strong> (empieza con 0% para aprobar todo)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
              <span>Activa <strong>&quot;Agente Global&quot;</strong> si quieres que aparezca en todas las páginas</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
              <span>Usa el botón <strong>&quot;Generar Mensaje&quot;</strong> para probar el agente</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">5</span>
              <span>Revisa las acciones en la pestaña <strong>&quot;Pendientes&quot;</strong> y aprueba o rechaza</span>
            </li>
          </ol>
        </div>
      </section>

      {/* CTA */}
      <div className="text-center pt-6 border-t border-gray-100">
        <Button 
          onClick={onCreateAgent}
          className="bg-amber-500 hover:bg-amber-600"
          size="lg"
        >
          <Plus size={18} className="mr-2" />
          Crear Mi Primer Agente
        </Button>
      </div>
    </div>
  );
};

export default AgentTutorial;
